import React, {
    useContext,
    createContext,
    PropsWithChildren,
    useMemo,
} from 'react'
import {
    useClinicianQueryInput,
    ClinicianQueryInputValues,
} from './query-input'
import {
    ClinicFieldsFragment,
    ClinicianFieldsFragment,
    useCountCliniciansQuery,
    useQueryCliniciansQuery,
    useQueryClinicsQuery,
} from '~graphql/generated/graphql'
import { useUserContext } from '~config/user-context'
import { eqStringFilter } from '~utils/query-helpers'

interface Props extends PropsWithChildren {
    clinic: ClinicFieldsFragment
}

export default function CliniciansProvider({ clinic, children }: Props) {
    const { queryInput, page, pageSize, ...queryInputValues } =
        useClinicianQueryInput(clinic)

    const { data, loading } = useQueryCliniciansQuery({
        variables: {
            where: queryInput,
            page,
            pageSize,
            orderBy: {
                column: 'is_active',
                isAscending: false,
            },
        },
        fetchPolicy: 'cache-and-network',
        skip: !clinic,
    })

    const { data: countData } = useCountCliniciansQuery({
        variables: {
            where: queryInput,
        },
        skip: !clinic,
    })

    const { isAdmin } = useUserContext()
    const { data: siblingClinicsData } = useQueryClinicsQuery({
        variables: {
            where: [eqStringFilter('pms_db', clinic.pms_db?.id)],
            pageSize: 50,
        },
        skip: !isAdmin || !clinic.pms_db?.id,
    })

    const locationClinics = useMemo(() => {
        const siblings = siblingClinicsData?.queryClinics ?? []
        if (siblings.some(sibling => sibling.id === clinic.id)) {
            return siblings
        }
        return [clinic, ...siblings]
    }, [clinic, siblingClinicsData?.queryClinics])

    const clinicians = data?.queryClinicians || []
    const count = countData?.countClinicians || 0

    return (
        <CliniciansContext.Provider
            value={{
                clinicians,
                loading,
                count,
                locationClinics,

                page,
                pageSize,

                ...queryInputValues,
            }}
        >
            {children}
        </CliniciansContext.Provider>
    )
}

export function useCliniciansContext() {
    return useContext(CliniciansContext)
}

type CliniciansContextType = {
    clinicians: ClinicianFieldsFragment[]
    loading: boolean
    count: number
    locationClinics: ClinicFieldsFragment[]
} & ClinicianQueryInputValues

const CliniciansContext = createContext<CliniciansContextType>(
    {} as CliniciansContextType,
)
